/**
 * Phase J.2 Pass 3 — Payment activity reconciliation widget for /admin.
 *
 * Renders 4 tiles (Collected / Pending / Failed / Refunded) for the
 * selected month. Each tile is a click-through to /admin/payments with
 * appropriate filters pre-applied. Month picker on the right.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "@/components/ui/card";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { TrendingUp, Clock, AlertTriangle, RotateCcw } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

function fmtMoney(cents) {
    if (cents == null) return "$0.00";
    const v = Math.abs(cents) / 100;
    return `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function makeMonthOptions() {
    const out = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
        const label = d.toLocaleDateString(undefined, { month: "long", year: "numeric" });
        out.push({ value, label });
    }
    return out;
}

export const PaymentActivityCard = () => {
    const monthOptions = useMemo(makeMonthOptions, []);
    const [month, setMonth] = useState(monthOptions[0].value);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = async (m) => {
        setLoading(true);
        try {
            const { data: d } = await api.get(`/admin/payments/dashboard-summary?month=${m}`);
            setData(d);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(month); }, [month]);

    const monthStart = data?.range?.from?.slice(0, 10) || "";
    const monthEnd = data?.range?.to?.slice(0, 10) || "";

    const tiles = [
        {
            key: "collected", label: "Collected", icon: TrendingUp,
            accent: "bg-emerald-50 text-emerald-900 ring-emerald-200",
            amount: data?.collected?.amount_cents,
            count: data?.collected?.count,
            href: `/admin/payments?status=succeeded&from=${monthStart}&to=${monthEnd}`,
        },
        {
            key: "pending", label: "Pending", icon: Clock,
            accent: "bg-amber-50 text-amber-900 ring-amber-200",
            amount: data?.pending?.amount_cents,
            count: data?.pending?.count,
            href: `/admin/payments?status=processing,requires_action,requires_confirmation`,
        },
        {
            key: "failed", label: "Failed", icon: AlertTriangle,
            accent: "bg-red-50 text-red-700 ring-red-200",
            amount: data?.failed?.amount_cents,
            count: data?.failed?.count,
            href: `/admin/payments?status=failed&from=${monthStart}&to=${monthEnd}`,
        },
        {
            key: "refunded", label: "Refunded", icon: RotateCcw,
            accent: "bg-purple-50 text-purple-900 ring-purple-200",
            amount: data?.refunded?.amount_cents,
            count: data?.refunded?.count,
            href: `/admin/payments?status=refunded,partially_refunded&from=${monthStart}&to=${monthEnd}`,
        },
    ];

    return (
        <Card className="col-span-1 md:col-span-2 lg:col-span-3" data-testid="payment-activity-card">
            <CardHeader className="flex flex-row items-start justify-between gap-3 pb-2">
                <div>
                    <CardTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
                        <TrendingUp size={16} className="text-blue-900" />
                        Payment activity
                    </CardTitle>
                    <CardDescription className="text-sm text-slate-500">
                        Stripe ACH activity for the selected month.
                    </CardDescription>
                </div>
                <Select value={month} onValueChange={setMonth}>
                    <SelectTrigger
                        className="h-9 w-[180px] text-xs"
                        data-testid="payment-activity-month"
                    >
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {monthOptions.map((o) => (
                            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </CardHeader>
            <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-1">
                {tiles.map((t) => {
                    const Icon = t.icon;
                    return (
                        <Link
                            key={t.key}
                            to={t.href}
                            data-testid={`payment-activity-tile-${t.key}`}
                            className={`rounded-lg ring-1 p-3 transition-colors hover:ring-2 ${t.accent}`}
                        >
                            <div className="flex items-center justify-between mb-1.5">
                                <span className="text-xs font-semibold uppercase tracking-wider">{t.label}</span>
                                <Icon size={14} />
                            </div>
                            <div className="font-display text-2xl font-bold tracking-tight tabular-nums">
                                {loading ? "…" : fmtMoney(t.amount)}
                            </div>
                            <div className="text-[11px] mt-0.5 opacity-75">
                                {loading ? "—" : `${t.count || 0} payment${(t.count || 0) === 1 ? "" : "s"}`}
                            </div>
                        </Link>
                    );
                })}
            </CardContent>
        </Card>
    );
};
